// Importa proyectos de Wokwi (diagram.json + código) al simulador 3D.
// Las posiciones 2D (px a 96 ppp) se pasan a mm sobre la mesa; las piezas montadas en la protoboard
// (conexiones con ruta "$bb") se colocan exactamente en sus agujeros.
import { PARTS, getDef, getFootprint, resolveProps, getPins } from './parts/index.js';
import { at } from './examples/helpers.js';
import { computeLayout } from './connectivity.js';
import { parseOhms } from './parts/units.js';

const PX = 25.4 / 96;
const LED_COLORS = ['red', 'orange', 'yellow', 'green', 'blue', 'white', 'purple'];
const num = (v, d) => (Number.isFinite(Number(v)) ? Number(v) : d);

/**
 * Tipo de Wokwi → { type, props(attrs), pins: {pinWokwi: pinNuestro}, rot }. rot: giro (°) que hay que sumarle
 * a la pieza para que quede como en Wokwi, si allá se dibuja en otra orientación.
 */
const MAP = {
  'wokwi-arduino-uno': { type: 'arduino-uno' },
  // En Wokwi la Pico va parada (USB arriba); aquí acostada (USB a la izquierda). Su pin 33 se llama GND.7 allá.
  'wokwi-pi-pico': { type: 'pi-pico', rot: 270, pins: { 'GND.7': 'AGND' } },
  'wokwi-arduino-nano': { type: 'arduino-nano', pins: { RESET: 'RESET.1' } },
  'wokwi-arduino-mega': { type: 'arduino-mega' },
  'wokwi-breadboard': { type: 'breadboard', props: () => ({ size: 'full' }) },
  'wokwi-breadboard-half': { type: 'breadboard', props: () => ({ size: 'half' }) },
  'wokwi-breadboard-mini': { type: 'breadboard', props: () => ({ size: 'mini' }) },
  'wokwi-led': { type: 'led', props: (a) => ({ color: LED_COLORS.includes(a.color) ? a.color : a.color === 'limegreen' ? 'green' : 'red' }) },
  'wokwi-resistor': { type: 'resistor', props: (a) => ({ value: parseOhms(a.value || '1000') || 1000 }) },
  'wokwi-pushbutton': { type: 'pushbutton', pins: { '1.l': '1a', '1.r': '1b', '2.l': '2a', '2.r': '2b' }, props: (a) => ({ color: ['red', 'green', 'blue', 'yellow', 'black', 'white'].includes(a.color) ? a.color : 'red', key: a.key || '' }) },
  'wokwi-pushbutton-6mm': { type: 'pushbutton', pins: { '1.l': '1a', '1.r': '1b', '2.l': '2a', '2.r': '2b' }, props: (a) => ({ color: ['red', 'green', 'blue', 'yellow', 'black', 'white'].includes(a.color) ? a.color : 'red', key: a.key || '' }) },
  'wokwi-potentiometer': { type: 'potentiometer', props: (a) => ({ value: a.value !== undefined ? num(a.value, 512) / 1023 : 0.5 }) },
  'wokwi-slide-potentiometer': { type: 'slide-potentiometer', props: (a) => ({ value: a.value !== undefined ? num(a.value, 512) / 1023 : 0.5 }) },
  'wokwi-slide-switch': { type: 'slide-switch', props: (a) => ({ position: a.value === '1' || a.value === 1 ? 'right' : 'left' }) },
  'wokwi-rgb-led': { type: 'rgb-led', props: (a) => ({ common: a.common === 'anode' ? 'anode' : 'cathode' }) },
  'wokwi-buzzer': { type: 'buzzer', pins: { 1: 'NEG', 2: 'POS' } },
  'wokwi-servo': { type: 'servo', props: (a) => ({ horn: a.horn === 'double' ? 'double' : a.horn === 'cross' ? 'cross' : 'single' }) },
  'wokwi-lcd1602': { type: (a) => (a.pins === 'i2c' ? 'lcd-i2c' : 'lcd'), props: (a) => ({ size: '16x2', address: a.i2cAddress === '0x3F' ? '0x3F' : '0x27' }) },
  'wokwi-lcd2004': { type: (a) => (a.pins === 'i2c' ? 'lcd-i2c' : 'lcd'), props: (a) => ({ size: '20x4', address: a.i2cAddress === '0x3F' ? '0x3F' : '0x27' }) },
  'board-ssd1306': { type: 'oled-ssd1306', pins: { DATA: 'SDA', CLK: 'SCL' } },
  'wokwi-7segment': {
    type: (a) => (num(a.digits, 1) > 1 ? 'seven-segment-4' : 'seven-segment'),
    pins: { DIG1: 'D1', DIG2: 'D2', DIG3: 'D3', DIG4: 'D4' },
    props: (a) => ({ common: a.common === 'anode' ? 'anode' : 'cathode', color: a.color && /green/.test(a.color) ? 'green' : 'red' }),
  },
  'wokwi-tm1637-7segment': { type: 'tm1637' },
  'wokwi-max7219-matrix': { type: (a) => (num(a.chain, 1) >= 4 ? 'max7219-matrix4' : 'max7219-matrix'), pins: { 'V+': 'VCC', 'V+.2': 'VCC.2' } },
  'wokwi-neopixel': { type: 'neopixel', pins: { VDD: 'VCC', VSS: 'GND' } },
  'wokwi-led-ring': { type: 'neopixel-ring', props: (a) => ({ pixels: [12, 16, 24].includes(num(a.pixels, 16)) ? num(a.pixels, 16) : 16 }) },
  'wokwi-neopixel-matrix': { type: 'neopixel-matrix' },
  'wokwi-led-bar-graph': { type: 'led-bar' },
  'wokwi-hc-sr04': { type: 'hc-sr04', props: (a) => (a.distance ? { distance: num(a.distance, 100) } : {}) },
  'wokwi-dht22': { type: 'dht22', props: (a) => ({ ...(a.temperature ? { temperature: num(a.temperature, 24) } : {}), ...(a.humidity ? { humidity: num(a.humidity, 40) } : {}) }) },
  'wokwi-pir-motion-sensor': { type: 'pir' },
  'wokwi-photoresistor-sensor': { type: 'photoresistor-module' },
  'wokwi-ntc-temperature-sensor': { type: 'ntc-module', pins: { OUT: 'S' } },
  'wokwi-analog-joystick': { type: 'joystick', pins: { VCC: '+5V', VERT: 'VRy', HORZ: 'VRx', SEL: 'SW' } },
  'wokwi-ky-040': { type: 'rotary-encoder', pins: { VCC: '+' } },
  'wokwi-membrane-keypad': { type: (a) => (a.columns === '3' ? 'keypad-3x4' : 'keypad') },
  'wokwi-mpu6050': { type: 'mpu6050' },
  'wokwi-ds1307': { type: 'ds1307', pins: { '5V': 'VCC' } },
  'wokwi-ir-receiver': { type: 'ir-receiver', pins: { DAT: 'OUT' } },
  'wokwi-hx711': { type: 'hx711' },
  'wokwi-relay-module': { type: 'relay-module' },
  'wokwi-74hc595': {
    type: '74hc595',
    pins: { Q0: 'QA', Q1: 'QB', Q2: 'QC', Q3: 'QD', Q4: 'QE', Q5: 'QF', Q6: 'QG', Q7: 'QH', Q7S: 'QHS', MR: 'SRCLR', SHCP: 'SRCLK', STCP: 'RCLK', DS: 'SER' },
  },
  'wokwi-ds18b20': { type: 'ds18b20', pins: { VCC: 'VDD' } },
  'wokwi-logic-analyzer': { type: 'logic-analyzer', pins: Object.fromEntries(Array.from({ length: 8 }, (_, i) => ['D' + i, 'CH' + i])) },
  'wokwi-gas-sensor': { type: 'gas-sensor', pins: { AOUT: 'AO', DOUT: 'DO' } },
  'wokwi-flame-sensor': { type: 'flame-sensor', pins: { AOUT: 'AO', DOUT: 'DO' } },
  'wokwi-small-sound-sensor': { type: 'sound-sensor', pins: { AOUT: 'AO', DOUT: 'DO' } },
  'wokwi-big-sound-sensor': { type: 'sound-sensor', pins: { AOUT: 'AO', DOUT: 'DO' } },
};

/** Pin de protoboard de Wokwi ("17t.e", "tp.3") → el nuestro ("e17", "tp3"). */
function bbPin(pin) {
  let m = /^(\d+)[tb]\.([a-j])$/.exec(pin);
  if (m) return m[2] + m[1];
  m = /^(t|b)([np])\.(\d+)$/.exec(pin);
  if (m) return m[1] + m[2] + m[3];
  return pin;
}

/**
 * Convierte un diagram.json de Wokwi. Devuelve { project, skipped: [tipos], dropped: n }.
 * files: { 'sketch.ino': código, … }
 */
export function importWokwi(diagram, files, name = 'Proyecto de Wokwi') {
  if (!diagram || !Array.isArray(diagram.parts)) throw new Error('El archivo no parece un diagram.json de Wokwi');
  const parts = [];
  const byId = new Map();
  const skipped = new Set();
  const pico = diagram.parts.find((p) => p.type === 'wokwi-pi-pico');
  if (pico && /micropython|circuitpython/i.test(String((pico.attrs || {}).env || ''))) {
    throw new Error('Este proyecto de Wokwi es de MicroPython: el simulador corre sketches de Arduino (C++), no Python.');
  }
  for (const wp of diagram.parts) {
    const m = MAP[wp.type];
    const attrs = wp.attrs || {};
    const type = m && (typeof m.type === 'function' ? m.type(attrs) : m.type);
    if (!m || !PARTS[type]) {
      skipped.add(wp.type);
      continue;
    }
    const props = m.props ? m.props(attrs) : {};
    const rot = ((360 - (num(wp.rotate, 0) % 360) + (m.rot || 0)) % 360 + 360) % 360;
    const f = getFootprint(type, resolveProps(type, props));
    // Con un cuarto de vuelta propio, la caja de Wokwi es la huella con ancho y fondo intercambiados.
    const quarter = (m.rot || 0) % 180 === 90 || (m.rot || 0) % 180 === -90;
    const fw = quarter ? f.d : f.w;
    const fd = quarter ? f.w : f.d;
    const id = String(wp.id).replace(/[^\w-]/g, '_');
    const part = { id, type, rot, props, x: num(wp.left, 0) * PX + fw / 2 - (quarter ? 0 : f.x), z: num(wp.top, 0) * PX + fd / 2 - (quarter ? 0 : f.z) };
    parts.push(part);
    byId.set(wp.id, { part, map: m });
  }
  const board = parts.find((p) => getDef(p.type).kind === 'board');
  if (!board) throw new Error('El diagrama no tiene una placa compatible (Uno, Nano, Mega o Raspberry Pi Pico)');
  const ox = board.x;
  const oz = board.z;
  for (const p of parts) {
    p.x -= ox;
    p.z -= oz;
  }
  const pinOf = (ref) => {
    const i = ref.indexOf(':');
    const e = byId.get(ref.slice(0, i));
    if (!e) return null;
    let pin = ref.slice(i + 1);
    if (e.part.type === 'breadboard') pin = bbPin(pin);
    else if (e.map.pins && e.map.pins[pin]) pin = e.map.pins[pin];
    const names = getPins(e.part.type, resolveProps(e.part.type, e.part.props)).map((x) => x.name);
    return names.includes(pin) ? { part: e.part.id, pin } : null;
  };
  // Piezas montadas en la protoboard: se colocan con el primer pin "$bb" y se revisa el resto.
  const conns = Array.isArray(diagram.connections) ? diagram.connections : [];
  const isBb = (c) => Array.isArray(c[3]) && c[3].includes('$bb');
  const placed = new Set();
  for (const c of conns) {
    if (!isBb(c)) continue;
    let [a, b] = [pinOf(c[0]), pinOf(c[1])];
    if (!a || !b) continue;
    const pa = parts.find((p) => p.id === a.part);
    if (pa.type === 'breadboard') [a, b] = [b, a];
    const part = parts.find((p) => p.id === a.part);
    const bb = parts.find((p) => p.id === b.part);
    if (!part || !bb || bb.type !== 'breadboard' || placed.has(part.id) || !getDef(part.type).insertable) continue;
    try {
      const pos = at(bb, b.pin, part.type, { rot: part.rot, pin: a.pin, props: part.props });
      part.x = pos.x;
      part.z = pos.z;
      placed.add(part.id);
    } catch {
      // agujero inexistente en nuestra protoboard: queda con cable
    }
  }
  // Si nuestra pieza no cae exactamente en los mismos agujeros (geometría distinta), se saca de la
  // protoboard y se conecta con cables: así nunca se crean uniones que no estaban en el original.
  const expected = new Map();
  for (const c of conns) {
    if (!isBb(c)) continue;
    let [a, b] = [pinOf(c[0]), pinOf(c[1])];
    if (!a || !b) continue;
    if (parts.find((p) => p.id === a.part).type === 'breadboard') [a, b] = [b, a];
    if (!expected.has(a.part)) expected.set(a.part, new Set());
    expected.get(a.part).add(`${a.pin}=${b.part}:${b.pin}`);
  }
  let layout = computeLayout({ parts, wires: [] });
  for (const id of placed) {
    const actual = new Set(layout.links.filter(([a]) => a.part === id).map(([a, b]) => `${a.pin}=${b.part}:${b.pin}`));
    const want = expected.get(id) || new Set();
    const same = actual.size === want.size && [...actual].every((k) => want.has(k));
    if (!same) {
      const part = parts.find((p) => p.id === id);
      const bb = parts.find((p) => p.id === [...want][0].split('=')[1].split(':')[0]);
      const f = getFootprint(bb.type, resolveProps(bb.type, bb.props));
      part.z = bb.z + f.d / 2 + 18;
    }
  }
  layout = computeLayout({ parts, wires: [] });
  const wires = [];
  let dropped = 0;
  const insertedLink = new Set(layout.links.map(([a, b]) => `${a.part}:${a.pin}=${b.part}:${b.pin}`));
  for (const c of conns) {
    const a = pinOf(c[0]);
    const b = pinOf(c[1]);
    if (!a || !b) {
      dropped++;
      continue;
    }
    if (isBb(c) && (insertedLink.has(`${a.part}:${a.pin}=${b.part}:${b.pin}`) || insertedLink.has(`${b.part}:${b.pin}=${a.part}:${a.pin}`))) continue;
    const color = typeof c[2] === 'string' && c[2] ? c[2] : 'green';
    wires.push({ id: 'w' + (wires.length + 1), a, b, color: /^#|^(green|red|black|blue|yellow|orange|white|purple|gray|brown)$/.test(color) ? color : 'green' });
  }
  return { project: { name, files, parts, wires }, skipped: [...skipped], dropped };
}
